import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import { ChangeLogButton } from '@app/components/ChangeLogButton';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { RemoveButton } from '@app/components/RemoveButton';
import { useEntity } from '@app/hooks/useEntity';
import { Country } from '@app/model/Country';
import { RedHatRegion } from '@app/model/RedHatRegion';
import { useCountryStore, useRedHatRegionStore } from '@app/store/entityStore';
import { ActionList, ActionListItem, Alert, AlertVariant, Bullseye, Button, Card, DualListSelector, DualListSelectorControl, DualListSelectorControlsWrapper, DualListSelectorList, DualListSelectorListItem, DualListSelectorPane, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter, Form, FormAlert, FormGroup, PageSection, Pagination, PaginationVariant, SearchInput, Split, SplitItem, Stack, TextInput, Title, Toolbar, ToolbarContent, ToolbarGroup, ToolbarItem, Tooltip } from '@patternfly/react-core';
import { AngleDoubleLeftIcon, AngleDoubleRightIcon, AngleLeftIcon, AngleRightIcon, PencilAltIcon, SearchIcon } from '@patternfly/react-icons';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { useBreadcrumbLabel } from "@app/hooks/useBreadcrumbLabel";

interface Option {
  country: Country;
  selected: boolean;
  isVisible: boolean;
  selectTimeStamp?: number;
}

type ValidateStatus = 'success' | 'warning' | 'error' | 'default';

const emptyRedHatRegion: Partial<RedHatRegion> = { name: '', countries: [] }

const RedHatRegions: React.FunctionComponent = () => {
  const { addNotification } = useNotification();
  useBreadcrumbLabel('Red Hat Regions');

  // Basic UI State
  const [page, setPage] = React.useState<number>(1);
  /**
   * The row whose change log is open, or null for none.
   *
   * A normalised {id, label} rather than the row itself: these three pages have no detail page, so
   * the panel is driven from here, and each of them names its rows with a different field.
   */
  const [changeLogRow, setChangeLogRow] = React.useState<{ id: number; label: string } | null>(null);
  const { perPage, setPerPage } = useTablePreferences('red-hat-regions', 10);
  const [filter, setFilter] = React.useState<string>('');
  const [isEditing, setIsEditing] = React.useState(false);

  // Data State
  const { items: rows, error, retry } = useEntity(useRedHatRegionStore);
  const saveRedHatRegion = useRedHatRegionStore(state => state.save);
  const deleteRedHatRegion = useRedHatRegionStore(state => state.delete);

  const { items: countries } = useEntity(useCountryStore);

  const [redHatRegion, setRedHatRegion] = React.useState<Partial<RedHatRegion>>(emptyRedHatRegion);
  const [validated, setValidated] = React.useState<Record<string, ValidateStatus>>({ name: 'default' });

  // Dual List Selector State
  const [availableOptions, setAvailableOptions] = React.useState<Option[]>([]);
  const [chosenOptions, setChosenOptions] = React.useState<Option[]>([]);
  const [availableFilter, setAvailableFilter] = React.useState('');
  const [chosenFilter, setChosenFilter] = React.useState('');

  // --- Derived State ---
  const filteredRows = React.useMemo(() => {
    if (!filter) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(filter.toLowerCase()));
  }, [rows, filter]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  // --- Handlers ---
  const handleNameChange = (_event: React.FormEvent, name: string) => {
    setValidated((prev) => ({ ...prev, name: name.trim().length > 0 ? 'success' : 'error' }));
    setRedHatRegion((prev) => ({ ...prev, name }));
  };

  const onSaveClick = async () => {
    if (!redHatRegion.name || redHatRegion.name.trim().length === 0) {
      setValidated((prev) => ({ ...prev, name: 'error' }));
      return;
    }

    beginLoading();
    try {
      const isNew = redHatRegion.id ? true : false;
      await saveRedHatRegion(redHatRegion, isNew );

      if (isNew) {
        addNotification('Red Hat Region updated with success', AlertVariant.success);
      } else {
        addNotification('Red Hat Region added with success.', AlertVariant.success);
      }
      resetForm();
    } catch (error: any) {
      addNotification(error.error || 'Operation failed', AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const onRemoveConfirm = async (_event: any, rhr: RedHatRegion) => {
    beginLoading();
    try {
      await deleteRedHatRegion(rhr.id);
      addNotification('Red Hat Region removed with success', AlertVariant.success);
      
      if (redHatRegion.id === rhr.id) {
        resetForm();
      }
    } catch (error: any) {
      addNotification(error.error || 'Removal failed', AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const resetForm = () => {
    setRedHatRegion(emptyRedHatRegion);
    // Safely reset available options from the master list
    setAvailableOptions(countries.map((country) => ({ country, selected: false, isVisible: true })));
    setChosenOptions([]);
    setAvailableFilter('');
    setChosenFilter('');
    setValidated({ name: 'default' });
    setIsEditing(false);
  };

  const onEditClick = (rhr: RedHatRegion) => {
    setRedHatRegion(rhr);
    setIsEditing(true);

    const newAvailable: Option[] = [];
    const newChosen: Option[] = [];

    // Rebuild options strictly from the master list to prevent missing options
    countries.forEach((country) => {
      const isChosen = rhr.countries?.some((c) => c.id === country.id);
      const cleanOpt = { country, selected: false, isVisible: true };
      if (isChosen) {
        newChosen.push(cleanOpt);
      } else {
        newAvailable.push(cleanOpt);
      }
    });

    setAvailableOptions(newAvailable);
    setChosenOptions(newChosen);
    setAvailableFilter('');
    setChosenFilter('');
  };

  // --- Dual List Handlers ---
  const onOptionSelect = (event: React.MouseEvent | React.ChangeEvent | React.KeyboardEvent, index: number, isChosen: boolean) => {
    const timeStamp = (event as React.MouseEvent).timeStamp;
    
    const updateOptions = (options: Option[]) => {
      const newOptions = [...options];
      const opt = newOptions[index]!;
      
      if (opt.selectTimeStamp && timeStamp - opt.selectTimeStamp < 250) {
        setTimeout(() => moveSelected(!isChosen), 0);
      } else {
        opt.selected = !opt.selected;
        opt.selectTimeStamp = timeStamp;
      }
      return newOptions;
    };

    if (isChosen) {
      setChosenOptions(updateOptions(chosenOptions));
    } else {
      setAvailableOptions(updateOptions(availableOptions));
    }
  };

  const moveSelected = (fromAvailable: boolean) => {
    const sourceOptions = fromAvailable ? availableOptions : chosenOptions;
    const destOptions = fromAvailable ? chosenOptions : availableOptions;

    const toMove = sourceOptions.filter((o) => o.selected && o.isVisible).map((o) => ({ ...o, selected: false, selectTimeStamp: undefined }));
    const toKeep = sourceOptions.filter((o) => !(o.selected && o.isVisible));

    const newSource = toKeep;
    const newDest = [...destOptions, ...toMove];

    if (fromAvailable) {
      setAvailableOptions(newSource);
      setChosenOptions(newDest);
      setRedHatRegion((prev) => ({ ...prev, countries: newDest.map((o) => o.country) }));
    } else {
      setChosenOptions(newSource);
      setAvailableOptions(newDest);
      setRedHatRegion((prev) => ({ ...prev, countries: newSource.map((o) => o.country) }));
    }
  };

  const moveAll = (fromAvailable: boolean) => {
    const sourceOptions = fromAvailable ? availableOptions : chosenOptions;
    const destOptions = fromAvailable ? chosenOptions : availableOptions;

    const toMove = sourceOptions.filter((o) => o.isVisible).map((o) => ({ ...o, selected: false, selectTimeStamp: undefined }));
    const toKeep = sourceOptions.filter((o) => !o.isVisible);

    const newSource = toKeep;
    const newDest = [...destOptions, ...toMove];

    if (fromAvailable) {
      setAvailableOptions(newSource);
      setChosenOptions(newDest);
      setRedHatRegion((prev) => ({ ...prev, countries: newDest.map((o) => o.country) }));
    } else {
      setChosenOptions(newSource);
      setAvailableOptions(newDest);
      setRedHatRegion((prev) => ({ ...prev, countries: newSource.map((o) => o.country) }));
    }
  };

  const onFilterChange = (value: string, isAvailable: boolean) => {
    if (isAvailable) {
      setAvailableFilter(value);
    } else {
      setChosenFilter(value);
    }
    const filterTerm = value.toLowerCase();
    
    const applyFilter = (options: Option[]) =>
      options.map((opt) => ({
        ...opt,
        isVisible: value === '' || opt.country.name.toLowerCase().includes(filterTerm)
      }));

    if (isAvailable) {
      setAvailableOptions((prev) => applyFilter(prev));
    } else {
      setChosenOptions((prev) => applyFilter(prev));
    }
  };

  const buildSearchInput = (isAvailable: boolean) => (
    <SearchInput
      value={isAvailable ? availableFilter : chosenFilter}
      onChange={(_event, value) => onFilterChange(value, isAvailable)}
      isDisabled={!isEditing}
      onClear={() => onFilterChange('', isAvailable)}
      aria-label={isAvailable ? 'Search available options' : 'Search chosen options'}
    />
  );

  // --- Render Helpers ---
  const buildPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(newPage);
      }}
      perPageOptions={[{ title: '5', value: 5 }, { title: '10', value: 10 }, { title: '20', value: 20 }, { title: '50', value: 50 }]}
      variant={variant as PaginationVariant}
      titles={{ paginationAriaLabel: `${variant} pagination` }}
    />
  );

  return (
    <EntityChangeLog
      entity="redhatregion"
      id={changeLogRow?.id}
      entityLabel="this region"
      title={changeLogRow?.label}
      isOpen={!!changeLogRow}
      onClose={() => setChangeLogRow(null)}
    >
      {/* Header Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Red Hat Regions</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              {/* NEW BUTTON PATTERN */}
              {isEditing ? (
                <>
                  <ActionListItem>
                    <Button variant="secondary" onClick={resetForm}>Cancel</Button>
                  </ActionListItem>
                  <ActionListItem>
                    <Button variant="primary" onClick={onSaveClick}>Save</Button>
                  </ActionListItem>
                </>
              ) : (
                <ActionListItem>
                  <Button variant="primary" onClick={() => setIsEditing(true)}>New Red Hat Region</Button>
                </ActionListItem>
              )}
              
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      {/* Editor Form Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Stack className="form-same-list-page">
          <Card isPlain>
            <div style={{ padding: '24px' }}>
              <Form isHorizontal id="form-partners">
                {validated.name === 'error' && (
                  <FormAlert>
                    <Alert variant="danger" title="Fill out all required fields before continuing." aria-live="polite" isInline />
                  </FormAlert>
                )}
                <FormGroup label="Name" isRequired fieldId="name">
                  <TextInput
                    isRequired type="text" id="name" name="name"
                    value={redHatRegion.name || ''} onChange={handleNameChange}
                    isDisabled={!isEditing} validated={validated.name}
                  />
                </FormGroup>
                
                {/* Only render DualListSelector if editing to save massive screen space otherwise */}
                {isEditing && (
                  <FormGroup label="Countries" fieldId="countries">
                    <DualListSelector>
                      <DualListSelectorPane
                        title="Available options"
                        isDisabled={!isEditing}
                        status={`${availableOptions.filter((o) => o.selected && o.isVisible).length} of ${availableOptions.filter((o) => o.isVisible).length} options selected`}
                        searchInput={buildSearchInput(true)}
                      >
                        <DualListSelectorList>
                          {availableOptions.map((option, index) =>
                            option.isVisible ? (
                              <DualListSelectorListItem key={option.country.id || index} isSelected={option.selected} onOptionSelect={(e) => onOptionSelect(e, index, false)}>
                                {option.country.name}
                              </DualListSelectorListItem>
                            ) : null
                          )}
                        </DualListSelectorList>
                      </DualListSelectorPane>

                      <DualListSelectorControlsWrapper>
                        <DualListSelectorControl isDisabled={!isEditing || !availableOptions.some((o) => o.selected)} onClick={() => moveSelected(true)} aria-label="Add selected" icon={<AngleRightIcon />} />
                        <DualListSelectorControl isDisabled={!isEditing || availableOptions.length === 0} onClick={() => moveAll(true)} aria-label="Add all" icon={<AngleDoubleRightIcon />} />
                        <DualListSelectorControl isDisabled={!isEditing || chosenOptions.length === 0} onClick={() => moveAll(false)} aria-label="Remove all" icon={<AngleDoubleLeftIcon />} />
                        <DualListSelectorControl isDisabled={!isEditing || !chosenOptions.some((o) => o.selected)} onClick={() => moveSelected(false)} aria-label="Remove selected" icon={<AngleLeftIcon />} />
                      </DualListSelectorControlsWrapper>

                      <DualListSelectorPane
                        title="Chosen options"
                        status={`${chosenOptions.filter((o) => o.selected && o.isVisible).length} of ${chosenOptions.filter((o) => o.isVisible).length} options selected`}
                        isDisabled={!isEditing} searchInput={buildSearchInput(false)} isChosen
                      >
                        <DualListSelectorList>
                          {chosenOptions.map((option, index) =>
                            option.isVisible ? (
                              <DualListSelectorListItem key={option.country.id || index} isSelected={option.selected} onOptionSelect={(e) => onOptionSelect(e, index, true)}>
                                {option.country.name}
                              </DualListSelectorListItem>
                            ) : null
                          )}
                        </DualListSelectorList>
                      </DualListSelectorPane>
                    </DualListSelector>
                  </FormGroup>
                )}
              </Form>
            </div>
          </Card>
        </Stack>
      </PageSection>

      {/* List / Table Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <PageSection hasBodyWrapper={false} isFilled>
          <Card>
            <div style={{ padding: '24px' }}>
              
              {/* FADE OVERLAY WRAPPER */}
              <div style={{ 
                opacity: isEditing ? 0.4 : 1, 
                pointerEvents: isEditing ? 'none' : 'auto', 
                transition: 'opacity 0.3s ease-in-out' 
              }}>
                
                <Toolbar id="compact-toolbar">
                  <ToolbarContent>
                    <ToolbarGroup>
                      <ToolbarItem>
                        <SearchInput
                          aria-label="name filter" placeholder="Filter by region name..."
                          onChange={(_ev, val) => { setFilter(val); setPage(1); }}
                          value={filter}
                          onClear={() => { setFilter(''); setPage(1); }}
                        />
                      </ToolbarItem>
                    </ToolbarGroup>
                    <ToolbarItem variant="pagination">{buildPagination('top', true)}</ToolbarItem>
                  </ToolbarContent>
                </Toolbar>
                
                {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
                    gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
                <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset-lg))', minHeight: 0, border: '1px solid #d2d2d2', borderRadius: '8px' }}>
                  <InnerScrollContainer>
                    <Table aria-label="Red Hat Regions Table" isStickyHeader variant='compact' gridBreakPoint="">
                      <Thead>
                        <Tr>
                          <Th width={25}>Name</Th>
                          <Th>Countries</Th>
                          <Th width={10} isStickyColumn stickyMinWidth="160px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {paginatedRows.map((row) => (
                          <Tr key={row.id || row.name} isRowSelected={redHatRegion.id === row.id}>
                            <Td dataLabel="Name"><b>{row.name}</b></Td>
                            <Td dataLabel="Countries">
                              {row.countries && row.countries.length > 0 
                                ? row.countries.map((c) => c.name).join(', ') 
                                : <span style={{ color: 'var(--pf-v6-global--Color--200)' }}>None assigned</span>
                              }
                            </Td>
                            <Td dataLabel="Actions" modifier="nowrap"
                              isStickyColumn stickyMinWidth="160px" stickyRightOffset="0" hasLeftBorder>
                              <ActionList>
                                <ActionListItem>
                                  <Tooltip content="Edit">
                                    {/* DISABLED PROP ADDED */}
                                    <Button icon={<PencilAltIcon />} variant="plain" onClick={() => onEditClick(row)} isDisabled={isEditing} />
                                  </Tooltip>
                                </ActionListItem>
                                <ActionListItem>
                                  {/* DISABLED PROP ADDED */}
                                  <RemoveButton confirmationMessage={`Confirm Red Hat Region "${row.name}" removal?`} isDisabled={isEditing} onRemoveConfirm={(ev) => onRemoveConfirm(ev, row)} />
                                </ActionListItem>
                                <ActionListItem>
                                  <ChangeLogButton recordLabel={row.name} onClick={() => setChangeLogRow({ id: row.id, label: row.name })} />
                                </ActionListItem>
                              </ActionList>
                            </Td>
                          </Tr>
                        ))}
                        {/* Nothing to show: distinguish "the load failed" from "no records match" */}
                        {error && filteredRows.length === 0 && (
                          <DataLoadErrorRow colSpan={3} error={error} entityLabel="Red Hat regions" onRetry={retry} />
                        )}
                        {!error && filteredRows.length === 0 && (
                          <Tr>
                            <Td colSpan={3}>
                              <Bullseye>
                                <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No results found">
                                  <EmptyStateBody>No Red Hat Regions match your search criteria.</EmptyStateBody>
                                  <EmptyStateFooter>
                                    <EmptyStateActions><Button variant="link" onClick={() => setFilter('')}>Clear search</Button></EmptyStateActions>
                                  </EmptyStateFooter>
                                </EmptyState>
                              </Bullseye>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </InnerScrollContainer>
                </OuterScrollContainer>
                {buildPagination('bottom', false)}
              </div>
              {/* END FADE OVERLAY WRAPPER */}
              
            </div>
          </Card>
        </PageSection>
      </PageSection>
    </EntityChangeLog>
  );
};

export { RedHatRegions };
